"use client";

import {
  getMainLayout,
  MainLayout,
  NavigationItem,
  updateMainLayoutFolderStructure,
} from "@/services/main-layout.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "./use-analytics";
import { useCallback, useMemo } from "react";
import { useAuthenticatedQuery } from "./use-authenticated-query";
import { useEntitiesFromDb } from "./schema/use-entities-from-db";
import { useCustomerId } from "@/contexts/customer-id-context";

export const useMainLayout = () => {
  const { entities } = useEntitiesFromDb();
  const customerId = useCustomerId();

  const { capture } = useAnalytics();

  const queryClient = useQueryClient();

  const { data: mainLayout } = useAuthenticatedQuery({
    queryKey: ["entity-instance-list", "MainLayout", customerId],
    queryFn: getMainLayout,
    enabled: !!entities?.map((entity) => entity.name).includes("MainLayout"),
  });

  const getCurrentEntityName = useCallback(
    (item: NavigationItem) => {
      if (item.type === "folder") {
        return item;
      }
      const entityName = entities?.find((entity) => entity.id === item.entity_id)?.name;
      if (entityName !== item.name) {
        return { ...item, name: entityName };
      }
      return item;
    },
    [entities],
  );

  const parsedMainLayout = useMemo(() => {
    if (
      typeof mainLayout?.folder_structure === "object" &&
      Array.isArray(mainLayout?.folder_structure)
    ) {
      const folderStructure = mainLayout.folder_structure.map(getCurrentEntityName);
      return {
        ...mainLayout,
        folder_structure: folderStructure,
      };
    }
    return mainLayout;
  }, [getCurrentEntityName, mainLayout]);

  const { mutate: updateFolderStructureMutation, mutateAsync: updateFolderStructureAsync } =
    useMutation({
      mutationFn: ({ id, folderStructure }: { id: string; folderStructure: NavigationItem[] }) => {
        capture("update_main_layout_folder_structure");
        return updateMainLayoutFolderStructure({
          id,
          folderStructure,
        });
      },

      // Add optimistic update
      onMutate: async ({ folderStructure }: { id: string; folderStructure: NavigationItem[] }) => {
        await queryClient.cancelQueries({
          queryKey: ["entity-instance-list", "MainLayout", customerId],
        });

        const previousData = queryClient.getQueryData([
          "entity-instance-list",
          "MainLayout",
          customerId,
        ]);

        queryClient.setQueryData(
          ["entity-instance-list", "MainLayout", customerId],
          (old: MainLayout) => {
            if (!old || !old.folder_structure) return old;

            const newData = {
              ...old,
              folder_structure: folderStructure,
            };

            return newData;
          },
        );

        return { previousData };
      },

      onError: (err, variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(
            ["entity-instance-list", "MainLayout", customerId],
            context.previousData,
          );
        }
      },

      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["entity-instance-list", "MainLayout", customerId],
        });
        queryClient.invalidateQueries({
          queryKey: ["permissions"],
          exact: false,
        });
      },
    });

  const { mutate: createInitialMainLayout, mutateAsync: createInitialMainLayoutAsync } =
    useMutation({
      mutationFn: async ({ layoutId }: { layoutId: string }) => {
        const languageEntity = entities?.find((x) => x.name === "Language");
        if (!languageEntity) {
          throw Error("Entity Language not found");
        }
        const structure: NavigationItem[] = [
          {
            id: languageEntity.id,
            entity_id: languageEntity.id,
            entity_name: languageEntity.name,
            show_in_nav: true,
            name: languageEntity.name,
            type: "entity",
            icon: "GlobeIcon",
            parentId: null,
          },
        ];
        return await updateFolderStructureAsync({
          id: layoutId,
          folderStructure: structure,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["entity-instance-list", "MainLayout", customerId],
        });
      },
    });

  return {
    mainLayout: parsedMainLayout,
    updateFolderStructure: updateFolderStructureMutation,
    updateFolderStructureAsync,
    createInitialMainLayout,
    createInitialMainLayoutAsync,
  };
};
